// Boxed hero + scanner scroll experience. The whole first screen is one sticky
// wrapper: an aggressively-rounded BOX (the stage) composites the scanner, and a
// text REGION below it holds the hero copy and, in its place, the per-x-ray
// captions. One scroll progress `p`, one rAF loop.
//
// The box (see home.css) stacks:
//   z0  walls          outside panels
//   z10 window/filmstrip [scan0, scan1, scan2, scan3]
//   z20 <canvas>       the video frames — a top compositing mask whose transparent
//                      screen + background reveal the layers behind it. The boot logo
//                      is baked into the video, not a separate overlay.
// The text region below the box holds the hero copy (fades out on scroll) and the
// per-x-ray captions (cross-fade in, one per active panel), stacked in one spot.
//
// Scroll model (one runway, one sticky wrapper):
//   Phase A [0, aEnd]     scrub frames 0→last inside the box; the hero copy fades out.
//                      Drawn at COVER scale — the scrub frames carry a baked floor
//                      that must bleed to the box edges.
//   Hold  [aEnd, holdEnd] dwell on the settled device; the drop-shadow rest frame fades
//                      in, and the ZOOM BEAT eases the frame from cover down to the
//                      capped device-fit scale (DEV_MAX) — the device "steps back" to
//                      open room for the walls before the x-ray begins.
//   Phase B [holdEnd, 1]  hold the last frame. A per-x-ray sequence: lead-in (panel 0),
//                      then for each panel — the background pins while its caption
//                      cross-fades in below the box, holds, then fades out as the
//                      background advances to the next panel. Walls + scans stay
//                      locked 1:1 (true x-ray) inside the box.
// Styles live in home.css (imported from main.js).

// Per-orientation frame geometry. Each render (landscape 16:9, portrait 9:16) has
// its own frame size, transparent screen-hole rect, and device-body bounds — all
// as fractions of the frame, measured from the HELD LAST frame of each set with
// tests/measure-screen-hole.mjs:
//   SRC=src/assets/videos/web_scroll/0540.png          node tests/measure-screen-hole.mjs
//   SRC=src/assets/videos/web_scroll_vertical/0540.png node tests/measure-screen-hole.mjs
// Re-measure and paste here whenever a render's device size/position changes.
const ORIENTS = {
  landscape: {
    FW: 1920, FH: 1080,
    SCREEN: { x: 0.2352, y: 0.2315, w: 0.4435, h: 0.5630 },
    // Device-body bbox as a fraction of the frame (measured on the held last frame via
    // tests/measure-screen-hole.mjs). Drives the aspect clamp so cover-fit never crops
    // the device. Landscape: device is tall-in-frame → over-wide boxes clip top/bottom.
    DEV: { w: 0.5310, h: 0.6500 },
  },
  portrait: {
    FW: 1080, FH: 1920,
    SCREEN: { x: 0.1505, y: 0.3839, w: 0.6065, h: 0.2437 },
    // Portrait: device is wide-in-frame (0.726) → over-tall boxes clip left/right instead.
    DEV: { w: 0.7259, h: 0.2815 },
  },
}
// Breathing room (fraction of the frame) kept on each edge the cover-fit would clip, so
// the device sits comfortably inside the box rather than flush against the gutter.
const SAFE_MARGIN = 0.04
// Phase-B device cap: once the x-ray begins, the frame is drawn at its own scale so
// the DEVICE BODY never exceeds these fractions of the box — that's what guarantees
// the walls real room around the device on every viewport (at cover-fit the aspect
// clamp lets the device reach ~89% of the box height on laptops). The frame scale
// eases cover→cap across the hold beat (the device "steps back"); it must stay at
// cover through ALL of phase A because the scrub frames carry a baked-in floor that
// bleeds to the frame edges (see tests/audit-frame-edges.mjs — only the edge-clean
// held frame + shadow frame are ever drawn below cover). w:0.74 ≈ the portrait
// cover fit, so phones see no zoom; h:0.70 is what shrinks the laptop 89%.
const DEV_MAX = { w: 0.74, h: 0.7 }
const ZOOM_END = 0.7 // fraction of the hold by which the cover→cap zoom completes
const easeInOut = (t) => 0.5 - 0.5 * Math.cos(Math.PI * t)
// Scan-legibility box morph (portrait). The wall/scan sources are landscape
// compositions (WALL); covering a TALL box scales them to the box height, which on
// phones magnified the scene ~3× past the box width — the device screen (a fixed
// window into the scene) showed only ~20% of the scan's width: illegible noise.
// PER-SLIDE fix: while a data-wall panel (full wall scene) is on screen, the box
// height eases down to the height at which the screen shows ≥ SCENE_MIN of the
// scene's width, and eases back to full height for the product/security panels
// (which read fine tall and use the tall canvas well). The tightened height only
// ever differs from full on portrait — landscape boxes sit at 36%+ naturally — and
// on portrait the device scale is width-driven, so the morph never changes the
// rendered device size: the box tightens AROUND the device. The morph eases over
// the middle of each panel transition (MORPH_W, in step-fraction units) and, for a
// data-wall panel 0, across the hold beat (the portrait sibling of the landscape
// device-zoom beat, same ZOOM_END timing).
const WALL = { W: 2560, H: 1441 } // scanner wall/scan source pixels
const SCENE_MIN = 0.3 // min fraction of the wall scene's width visible through the screen
const MORPH_W = [0.3, 0.7] // slice of each panel step over which the box height morphs
// How much of the wall-tightening the PRODUCT/security slides keep (0 = re-open to
// the full tall card, 1 = stay as tight as the wall slides). A partial value reins
// in the "zoom back in" on phones — the product card re-opens most of the way, not
// all the way. Only ever has an effect on portrait: landscape has wallH === fullH,
// so any mix lands on the same height there. The scrub/hero always runs at 0 (full).
const PRODUCT_MIX = 1
// Single responsive split: use the portrait (9:16) render for tall boxes, the
// landscape (16:9) render for wide ones. The decision is keyed to the BOX AREA's
// actual aspect (measured in pickOrient, once the DOM exists) with a hysteresis
// band so iOS toolbar show/hide or small resizes near the threshold can't thrash
// a full frame-set swap. Verified against real devices: phones 0.55–0.9 (portrait),
// landscape phones ~1.5, iPads 0.87/2.4, laptops 2.2+ — only squarish desktop
// windows land inside the sticky band. The viewport MQ is just the pre-layout seed.
const ORIENT_BAND = { toPortrait: 1.3, toLandscape: 1.45 }
const portraitMql = window.matchMedia('(max-aspect-ratio: 7/6)')
let orient = portraitMql.matches ? 'portrait' : 'landscape'
let cfg = ORIENTS[orient]
// Wall/scan sources are 2560px wide — past that, cover-fit upscales them beyond 1:1
// on every DPI, so the box (and with it the letterbox) stops growing there.
const MAX_STAGE_W = 2560
const FRAME_COUNT = 121
// First frame of the scrub (0-based: 0 = frame_0001). Bump this to start the video
// partway in — the scrub then runs START_FRAME → last, and the earlier frames are
// neither shown nor fetched. Must stay < FRAME_COUNT - 1.
const START_FRAME = 11

const PHASE_A_VH = 1 // scroll (viewport-heights) for the video scrub
// Hold beat after the scrub: the device has fully assembled, so we dwell on the
// settled "rest" frame — the polished final render WITH its drop shadow (final_shadow.webp)
// composited over the elevated-black stage — before any wall/scan scrolls up behind it.
// The shadow fades in across this hold (REST_IN of it) and fades back out as the first
// wall rises in phase B (REST_OUT wp-units), because the shadow is baked onto the black
// backdrop and would otherwise smear over the x-ray walls.
const HOLD_VH = 0.3 // long enough for the cover→cap zoom beat to breathe (was 0.1)
const REST_IN = 0.6 // fraction of the hold over which the shadow fades in
const REST_OUT = 0.5 // wp-units of the lead-in over which the shadow fades back out

// Phase B advances the walls/scans CONTINUOUSLY (no pinning) so something is always
// moving as you scroll — it never feels "stuck" or scroll-jacky. The wall position is
// tracked in "panel units" wp: it runs from -1 (panel 0 fully below the frame) to N-1
// (the last panel centred). Each caption snaps in the instant its panel is halfway
// onto the screen and holds until the next one takes over.
const N = 4
const LEAD_VH = 1 // panel 0 rises from below to centred (wp: -1 → 0)
const STEP_VH = 1 // scroll (viewport-heights) per subsequent panel advance (wp: k → k+1)
const CAP_SNAP = 0.14 // wp-units of the quick caption fade — near-instant, not a long cross-fade
const PHASE_B_VH = LEAD_VH + (N - 1) * STEP_VH
const TOTAL_VH = PHASE_A_VH + HOLD_VH + PHASE_B_VH
const aEnd = PHASE_A_VH / TOTAL_VH // end of the video scrub (device fully assembled)
const holdEnd = (PHASE_A_VH + HOLD_VH) / TOTAL_VH // end of the rest-frame hold / start of x-ray

// wp (wall position, in panel units) as a function of the phase-B beat b (0…PHASE_B_VH).
const wpAt = (b) => (b <= LEAD_VH ? -1 + b / LEAD_VH : (b - LEAD_VH) / STEP_VH)

// Hero copy fade-out. The hero headline/subtext/button fade to nothing over the
// first slice of phase A, before the scrub completes — the box takes over the frame.
const HERO_FADE_END = aEnd * 0.55

const EXPAND = 0.01 // grow the window 1% each side; the bezel hides the overscan
const MAX_DPR = 2

const base = import.meta.env.BASE_URL
const framePath = (n) => `${base}scroll/${orient}/frame_${String(n).padStart(4, '0')}.webp`
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v))
const px = (n) => `${n}px`

// Caption k is lit while its panel is the one on screen: wp ∈ [k-0.5, k+0.5), with a
// quick snap fade at each boundary so it reads as "appears the moment the image is
// halfway on", not a long cross-fade. The last panel stays lit through the release.
const capOpAt = (k, wp) => {
  const start = k - 0.5
  if (wp < start) return 0
  if (wp < start + CAP_SNAP) return (wp - start) / CAP_SNAP // quick fade in
  if (k === N - 1) return 1 // last panel: stay lit through the release
  const end = k + 0.5
  if (wp < end - CAP_SNAP) return 1
  if (wp < end) return 1 - (wp - (end - CAP_SNAP)) / CAP_SNAP // quick fade out
  return 0
}
// p at which each panel is centred / its caption is at rest (wp = k) — exposed for tests.
const panelCenterP = Array.from({ length: N }, (_, k) => holdEnd + (LEAD_VH + k * STEP_VH) / TOTAL_VH)
const capRestP = panelCenterP.slice()

const section = document.querySelector('[data-experience]')
const sticky = section?.querySelector('[data-exp-sticky]') // pinned wrapper (100svh)
const stage = section?.querySelector('[data-exp-stage]') // the rounded box
const area = section?.querySelector('[data-exp-area]') // flex cell that centers the box
const canvas = section?.querySelector('[data-exp-canvas]')
const hud = section?.querySelector('[data-exp-hud]')
const scrollHint = section?.querySelector('[data-scroll-hint]')

if (section && stage && area && canvas) {
  const ctx = canvas.getContext('2d')
  const captions = [...section.querySelectorAll('[data-caption]')]
  // Which panels are FULL WALL SCENES (box tightens for legibility on portrait) vs
  // product/security shots (box stays tall). Authored in index.html via data-wall.
  const wallFlags = [...section.querySelectorAll('.exp-walls .exp-panel')].map((el) => el.hasAttribute('data-wall'))
  const hudOn = new URLSearchParams(location.search).get('debug') === '1'
  if (hudOn) section.classList.add('debug')

  // Orientation from the measured box-area aspect, sticky inside the hysteresis
  // band. Falls back to the current pick when the aspect sits between thresholds.
  const pickOrient = () => {
    const a = area.clientWidth / Math.max(1, area.clientHeight)
    if (a <= ORIENT_BAND.toPortrait) return 'portrait'
    if (a >= ORIENT_BAND.toLandscape) return 'landscape'
    return orient
  }
  // Re-pick now that the DOM is measurable (the module-scope init could only see
  // the viewport MQ); loadFrames below then fetches the right set first.
  orient = pickOrient()
  cfg = ORIENTS[orient]

  section.style.height = `${(1 + TOTAL_VH) * 100}svh` // sticky screen + scrub + scan (svh: stable under iOS toolbar)
  section.dataset.aend = aEnd.toFixed(4) // exposed for tests / debug
  section.dataset.holdend = holdEnd.toFixed(4)
  section.dataset.panelsp = panelCenterP.map((v) => v.toFixed(4)).join(',')
  section.dataset.captionsp = capRestP.map((v) => v.toFixed(4)).join(',')
  section.dataset.orient = orient

  // Wall/scan images live in public/scanner/; resolve against Vite's base.
  section.querySelectorAll('img[data-src]').forEach((img) => {
    img.src = `${base}scanner/${img.dataset.src}.webp`
  })

  // --- video frames -----------------------------------------------------------
  // Per-orientation cache: each set is fetched at most once per page life, so a
  // flip across the split (iPad rotation, window resize) never re-downloads or
  // re-decodes — it just swaps which cached set the draw reads from.
  const frameSets = { landscape: null, portrait: null }
  let frames = new Array(FRAME_COUNT)
  const ready = (img) => img && img.complete && img.naturalWidth > 0
  // The rest frame: the settled device WITH its drop shadow, held during the hold beat
  // and composited over the plain last video frame (restMix). Its screen hole + device
  // body align pixel-for-pixel with frame[last], so mixing it in never shifts the device.
  let restFrame = new Image()
  // Point `frames`/`restFrame` at the active orientation's set, fetching it on first
  // use. Load callbacks guard on the orientation still being active, so a stale
  // set's arrivals never repaint over the current one.
  const loadFrames = () => {
    const key = orient
    let entry = frameSets[key]
    const cached = !!entry
    if (!entry) {
      entry = { frames: new Array(FRAME_COUNT), rest: new Image() }
      frameSets[key] = entry
      for (let i = START_FRAME; i < FRAME_COUNT; i++) {
        const img = new Image()
        img.decoding = 'async'
        img.src = framePath(i + 1)
        entry.frames[i] = img
        if (i === START_FRAME || i === FRAME_COUNT - 1) img.onload = () => { if (orient === key) drawFrame(lastFrame < 0 ? START_FRAME : lastFrame) }
      }
      entry.rest.decoding = 'async'
      entry.rest.src = `${base}scroll/${key}/final_shadow.webp`
      entry.rest.onload = () => { if (orient === key) drawFrame(lastFrame < 0 ? START_FRAME : lastFrame) }
    }
    frames = entry.frames
    restFrame = entry.rest
    // Swapping to an already-cached set: repaint immediately (first load paints via
    // the onload hooks above; drawFrame isn't defined yet on the very first call).
    if (cached) drawFrame(lastFrame < 0 ? START_FRAME : lastFrame)
  }
  loadFrames()

  // Re-pick the orientation from the box-area aspect; if it changed, swap the active
  // geometry config and frame set. Returns whether it flipped.
  const syncOrient = () => {
    const want = pickOrient()
    if (want === orient) return false
    orient = want
    cfg = ORIENTS[orient]
    section.dataset.orient = orient
    loadFrames()
    return true
  }
  const nearestFrame = (idx) => {
    if (ready(frames[idx])) return frames[idx]
    for (let d = 1; d < FRAME_COUNT; d++) {
      if (ready(frames[idx - d])) return frames[idx - d]
      if (ready(frames[idx + d])) return frames[idx + d]
    }
    return null
  }

  // --- geometry ---------------------------------------------------------------
  const dpr = () => Math.min(window.devicePixelRatio || 1, MAX_DPR)
  const snap = (n) => Math.round(n * dpr()) / dpr()
  const set = (name, v) => section.style.setProperty(name, v)
  // scale (CSS px per frame px) is what the canvas draw AND the window rect share:
  // coverScale fills the box (phase A — the floor needs full bleed), capScale caps
  // the device at DEV_MAX (phase B — the walls need room). zoomEased blends them.
  // The box itself has two endpoint heights: fullH (scrub + product slides) and
  // wallH (legibility-tightened, data-wall slides); boxMix blends those, and
  // stageH is whichever blend is currently applied to the DOM.
  const geom = { stageW: 0, stageH: 0, fullH: 0, wallH: 0, winW: 0, winH: 0, coverScale: 0, capScale: 0, scale: 0 }
  let zoomEased = 0 // 0 = cover (scrub), 1 = capped (x-ray); eased across the hold
  let boxMix = 0 // 0 = full box, 1 = legibility-tightened (wall slides); eased
  const morphTargetH = () => Math.round(geom.fullH + (geom.wallH - geom.fullH) * boxMix)

  const setGeometry = () => {
    // Pull the active orientation's frame size + screen/device fractions.
    const { FW, FH, SCREEN, DEV } = cfg
    // Clamp the box aspect to the device-safe window so cover-fit never crops the
    // device. The visible crop of a cover-fit is a centred rect of the box's aspect:
    //   too WIDE (a > maxA) → frame taller than box → clips top/bottom → pull width in
    //   too TALL (a < minA) → frame wider than box → clips left/right → pull height in
    // maxA/minA come from the device's height/width extents (+ margin). The frame's own
    // aspect always sits inside [minA, maxA], so a valid fit always exists. Whatever
    // size we set, the [data-exp-area] flex cell centres it — that's the letterbox.
    const frameA = FW / FH
    const maxA = frameA / (DEV.h + 2 * SAFE_MARGIN)
    const minA = frameA * (DEV.w + 2 * SAFE_MARGIN)
    const availW = Math.min(area.clientWidth, MAX_STAGE_W) // don't grow past the wall sources' 1:1
    const availH = area.clientHeight
    let stageW = availW
    let stageH = availH
    const a = availW / availH
    if (a > maxA) stageW = Math.round(availH * maxA) // over-wide → side gutters (ultrawide)
    else if (a < minA) stageH = Math.round(availW / minA) // over-tall → top/bottom gutters (tall phones)
    // stageH is the FULL box height (scrub + product slides). Derive the tightened
    // height for data-wall slides: cap it so the screen shows ≥ SCENE_MIN of the
    // wall scene's width. Exact in one pass on portrait (the screen window's width
    // is box-width-driven there); the guard breaks immediately wherever the wall
    // cover is width-driven (all landscape boxes — scene fraction is 36%+ there,
    // so wallH === fullH and the morph is a no-op).
    let wallH = stageH
    for (let i = 0; i < 3; i++) {
      if (wallH / WALL.H <= stageW / WALL.W) break // wall cover width-driven → height can't help
      const cover = Math.max(stageW / FW, wallH / FH)
      const cap = Math.min(cover, (DEV_MAX.w * stageW) / (DEV.w * FW), (DEV_MAX.h * wallH) / (DEV.h * FH))
      const winW = SCREEN.w * FW * cap * (1 + 2 * EXPAND)
      if (winW / (WALL.W * Math.max(stageW / WALL.W, wallH / WALL.H)) >= SCENE_MIN - 1e-6) break
      wallH = Math.floor((winW / SCENE_MIN) * (WALL.H / WALL.W))
    }
    Object.assign(geom, { stageW, fullH: stageH, wallH })
    section.dataset.fullh = stageH // exposed for tests
    section.dataset.wallh = wallH
    stage.style.width = px(stageW)
    set('--box-w', px(stageW)) // the copy region below tracks the box width so it stays aligned
    set('--stage-w', px(stageW))
    // The canvas is pinned to the FULL height (centred over the morphing stage, see
    // .exp-canvas) so its buffer never reallocates mid-morph — the stage's
    // overflow:hidden clips it while the box is tightened.
    set('--stage-full-h', px(stageH))
    setStageH(morphTargetH())
    resizeCanvas()
  }

  // Apply a stage height (the box-morph blend) and everything derived from it: the
  // endpoint frame scales, the screen-hole window, and the CSS vars the wall/scan
  // layers read. Called on resize and on every tick where the morph is mid-flight.
  const setStageH = (h) => {
    const { FW, FH, DEV } = cfg
    geom.stageH = h
    stage.style.height = px(h)
    set('--stage-h', px(h))
    // Two endpoint scales for the frame (the walls/scans keep their own object-fit:
    // cover — they always fill the box regardless of the frame scale):
    //   coverScale — frame cover-fills the box (phase A: the baked floor must bleed
    //                to every edge; the aspect clamp above guarantees no device crop)
    //   capScale   — device body ≤ DEV_MAX of the box (phase B: wall breathing room)
    // On portrait both are width-driven, so the morph leaves the device size intact.
    const cover = Math.max(geom.stageW / FW, h / FH)
    geom.coverScale = cover
    geom.capScale = Math.min(cover, (DEV_MAX.w * geom.stageW) / (DEV.w * FW), (DEV_MAX.h * h) / (DEV.h * FH))
    updateFrameGeom()
  }

  // Position the screen-hole window (and record the live frame scale) for the current
  // zoom blend. Runs on resize and on every tick where the zoom is mid-flight — the
  // canvas draw uses the same geom.scale, so mask and pixels stay locked.
  const updateFrameGeom = () => {
    const { FW, FH, SCREEN } = cfg
    const s = geom.coverScale + (geom.capScale - geom.coverScale) * zoomEased
    geom.scale = s
    const fW = FW * s
    const fH = FH * s
    const fLeft = (geom.stageW - fW) / 2
    const fTop = (geom.stageH - fH) / 2
    const padX = SCREEN.w * fW * EXPAND
    const padY = SCREEN.h * fH * EXPAND
    geom.winW = SCREEN.w * fW + 2 * padX
    geom.winH = SCREEN.h * fH + 2 * padY
    set('--win-left', px(snap(fLeft + SCREEN.x * fW - padX)))
    set('--win-top', px(snap(fTop + SCREEN.y * fH - padY)))
    set('--win-w', px(snap(geom.winW)))
    set('--win-h', px(snap(geom.winH)))
  }

  const resizeCanvas = () => {
    const d = dpr()
    canvas.width = Math.round(canvas.clientWidth * d)
    canvas.height = Math.round(canvas.clientHeight * d)
    drawFrame(lastFrame < 0 ? START_FRAME : lastFrame)
  }

  let lastFrame = -1
  let lastRest = -1
  let lastScale = -1
  // Draw at the SAME scale the window geometry uses (geom.scale — cover during the
  // scrub, capped during the x-ray), centred like the CSS frame rect, so the canvas
  // pixels and the screen-hole mask can never drift apart.
  const drawScaled = (img, alpha) => {
    const d = dpr()
    const dw = img.naturalWidth * geom.scale * d
    const dh = img.naturalHeight * geom.scale * d
    ctx.globalAlpha = alpha
    ctx.drawImage(img, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh)
    ctx.globalAlpha = 1
  }
  const drawFrame = (idx) => {
    ctx.clearRect(0, 0, canvas.width, canvas.height) // transparent → composites the layers behind through
    const img = nearestFrame(idx)
    if (img) drawScaled(img, 1)
    // The shadow rest frame rides on top at restMix; its device pixels match the base
    // frame's exactly, so only the drop shadow ramps in/out — no ghosting on the device.
    if (restMix > 0.001 && ready(restFrame)) drawScaled(restFrame, restMix)
    lastFrame = idx
    lastRest = restMix
    lastScale = geom.scale
  }

  // --- scroll model -----------------------------------------------------------
  // Box-morph mix as a function of the wall position wp: each panel k targets 1
  // (tightened) if it's a data-wall scene, PRODUCT_MIX (mostly re-opened) otherwise;
  // between panels the mix eases across the MORPH_W slice of the step. Constant
  // wherever neighbours agree, so the box only moves during a wall↔product handover.
  const panelMix = (k) => (wallFlags[k] ? 1 : PRODUCT_MIX)
  const boxMixAt = (wp) => {
    if (wp <= 0) return panelMix(0)
    const k = Math.floor(wp)
    if (k >= N - 1) return panelMix(N - 1)
    const from = panelMix(k)
    const to = panelMix(k + 1)
    if (from === to) return from
    return from + (to - from) * easeInOut(clamp((wp - k - MORPH_W[0]) / (MORPH_W[1] - MORPH_W[0]), 0, 1))
  }

  let wallY = 0
  let screenY = 0
  let frameIdx = 0
  let beatB = 0
  let activeCap = -1
  let heroOp = 1 // hero-copy opacity (fades out on the first scroll)
  let restMix = 0 // shadow rest-frame opacity (0 during scrub, 1 during the hold, fades out as walls rise)
  const capOp = new Array(N).fill(0)
  const computeScroll = () => {
    const rect = section.getBoundingClientRect()
    // Scroll travel = runway height minus the pinned wrapper (one viewport).
    const dist = section.offsetHeight - (sticky?.clientHeight || window.innerHeight)
    const p = dist > 0 ? clamp(-rect.top / dist, 0, 1) : 0

    heroOp = clamp(1 - p / HERO_FADE_END, 0, 1)

    if (p <= aEnd) {
      // Phase A — scrub the video (boot logo baked in); walls parked below (hidden);
      // window parked (screenY == H) so no scan is in view; captions off; no shadow yet.
      const pa = aEnd > 0 ? p / aEnd : 0
      frameIdx = START_FRAME + Math.round(pa * (FRAME_COUNT - 1 - START_FRAME))
      boxMix = 0 // full box for the whole scrub
      const H = morphTargetH()
      wallY = H
      screenY = H // window parked one screen down — no scan in view during the scrub
      restMix = 0
      zoomEased = 0 // cover-fit throughout the scrub — the baked floor must bleed to the box edges
      for (let k = 0; k < capOp.length; k++) capOp[k] = 0
      beatB = 0
      activeCap = -1
    } else if (p <= holdEnd) {
      // Hold — device assembled and parked on the last frame; walls still below (hidden);
      // the drop shadow fades in so the settled device reads before any x-ray scroll.
      const ph = holdEnd > aEnd ? (p - aEnd) / (holdEnd - aEnd) : 1
      frameIdx = FRAME_COUNT - 1
      restMix = clamp(ph / REST_IN, 0, 1)
      // The zoom beat: the settled device steps back from cover to the capped scale,
      // opening the room the walls will scroll through. Only the held last frame and
      // the shadow frame draw here — both edge-clean (tests/audit-frame-edges.mjs).
      // On portrait the same beat drives the BOX tighten instead — the card closes
      // in (to panel 0's target mix) around the constant-size device.
      zoomEased = easeInOut(clamp(ph / ZOOM_END, 0, 1))
      boxMix = zoomEased * panelMix(0)
      const H = morphTargetH()
      wallY = H
      screenY = H
      for (let k = 0; k < capOp.length; k++) capOp[k] = 0
      beatB = 0
      activeCap = -1
    } else {
      // Phase B — hold last frame; walls/scans advance continuously (x-ray); captions snap.
      const b = (p - holdEnd) * TOTAL_VH
      const wp = wpAt(b)
      beatB = b
      boxMix = boxMixAt(wp) // box tightens for wall scenes, opens back up for products
      const H = morphTargetH()
      wallY = -wp * H // continuous: wp runs -1 → N-1, so the walls/scans always move with scroll
      screenY = wallY // scans ride the filmstrip locked 1:1 to the walls (true x-ray)
      frameIdx = FRAME_COUNT - 1
      zoomEased = 1 // device holds at the capped scale for the whole x-ray
      // Fade the shadow back out as the first wall rises (wp: -1 → -1+REST_OUT) — the baked
      // shadow lives on the black backdrop and must be gone before the wall reaches it.
      restMix = clamp(1 - (wp + 1) / REST_OUT, 0, 1)
      activeCap = -1
      for (let k = 0; k < capOp.length; k++) {
        capOp[k] = capOpAt(k, wp)
        if (capOp[k] > 0.01) activeCap = k
      }
    }
    return p
  }

  const apply = (p) => {
    set('--wall-y', px(snap(wallY)))
    set('--screen-y', px(snap(screenY)))
    for (let k = 0; k < captions.length; k++) {
      captions[k].style.setProperty('--cap-op', capOp[k].toFixed(3))
    }
    set('--hero-op', heroOp.toFixed(3)) // fades the in-box headline + the subtext/CTA below it
    if (scrollHint) scrollHint.style.opacity = heroOp.toFixed(3)
    // Mid-morph: apply the blended box height (which re-derives the scales and the
    // window in one go). Otherwise, mid-zoom only: move the window mask to the
    // blended scale. Either way redraw the canvas if its drawn scale moved.
    const h = morphTargetH()
    if (h !== geom.stageH) setStageH(h)
    else {
      const s = geom.coverScale + (geom.capScale - geom.coverScale) * zoomEased
      if (s !== geom.scale) updateFrameGeom()
    }
    if (frameIdx !== lastFrame || restMix !== lastRest || geom.scale !== lastScale) drawFrame(frameIdx)
    if (hudOn) updateHud(p)
  }

  const updateHud = (p) => {
    if (!hud) return
    const phase = p <= aEnd ? 'A scrub' : p <= holdEnd ? 'hold' : 'B scan'
    hud.textContent = [
      `p         ${p.toFixed(3)}  (${phase})`,
      `frame     ${frameIdx + 1}/${FRAME_COUNT}`,
      `beat/cap  ${beatB.toFixed(2)} / ${activeCap}`,
      `wall-y    ${wallY.toFixed(1)}`,
      `rest-mix  ${restMix.toFixed(3)}`,
      `hero-op   ${heroOp.toFixed(3)}`,
      `zoom      ${zoomEased.toFixed(3)}  scale ${geom.scale.toFixed(4)} (cover ${geom.coverScale.toFixed(4)} cap ${geom.capScale.toFixed(4)})`,
      `box       mix ${boxMix.toFixed(3)}  H ${geom.stageH} (full ${geom.fullH} wall ${geom.wallH})`,
      `stage     ${geom.stageW}×${geom.stageH}`,
    ].join('\n')
  }

  // Measure the tallest caption at the current wrap width/font. They're absolutely
  // positioned (opacity-only visibility), so this is a pure read — no layout shift.
  const measureReserve = () => {
    let max = 0
    for (const c of captions) max = Math.max(max, c.offsetHeight)
    return max
  }
  // Reserve the copy region for the tallest caption, then re-run geometry until
  // stable: when the aspect clamp is active, a taller reserve shrinks the box, which
  // narrows the captions' wrap width — but --cap-fs scales with the box width
  // (home.css), so the line count stays put and the loop damps down; hard-capped at
  // 4 passes. +4px buffer absorbs sub-pixel wrap jitter across the font swap.
  // (Row-mode CSS ignores the reserve entirely.)
  let lastReserve = -1
  const settleLayout = () => {
    setGeometry()
    // Safety clamp: the reserve may never eat more than 45% of the pinned screen —
    // if a measurement pathologically explodes (zero-width column, missing font),
    // the box survives and the caption overflow is the visible symptom, not a
    // collapsed experience.
    const rCap = 0.45 * (sticky?.clientHeight || window.innerHeight)
    for (let i = 0; i < 4; i++) {
      const r = Math.min(measureReserve() + 4, rCap)
      if (Math.abs(r - lastReserve) < 1) break
      lastReserve = r
      set('--cap-reserve', px(r))
      setGeometry()
    }
    apply(computeScroll())
  }

  settleLayout()

  let rafId = null
  const tick = () => {
    apply(computeScroll())
    rafId = requestAnimationFrame(tick)
  }
  const start = () => { if (rafId == null) rafId = requestAnimationFrame(tick) }
  const stop = () => { if (rafId != null) { cancelAnimationFrame(rafId); rafId = null } }

  const onResize = () => {
    syncOrient() // flip landscape/portrait + swap frame sets if the split was crossed
    settleLayout() // geometry + measured caption reserve (converging), then apply
  }
  window.addEventListener('resize', onResize, { passive: true })
  window.addEventListener('orientationchange', onResize)
  // Söhne loads async; until it swaps in the copy region is measured in the fallback
  // font, so the box is sized against the wrong region height and overlaps the copy.
  // Recompute once fonts are ready (the "subtext under the box until you resize" bug).
  document.fonts?.ready.then(onResize)
  new IntersectionObserver(([e]) => {
    if (e.isIntersecting) { start(); return }
    // Section out of view: stop the loop after snapping to the correct end-state.
    apply(computeScroll())
    stop()
  }).observe(section)
}
